/**
 * Supervisor view of team attendance.
 *
 * Anomaly flags are shown but never presented as a verdict — a bad GPS fix and
 * a spoofed one look identical from here, so the screen surfaces them for a
 * human to judge rather than labelling anyone.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, ShieldAlert } from 'lucide-react';
import { client } from '../api/client';
import { flagLabels } from '../lib/anomalyFlags';
import AttendanceDetailPanel from '../components/AttendanceDetailPanel';
import EmployeeDetailPanel from '../components/EmployeeDetailPanel';
import type { AttendanceDay, LiveTeamMember, Page } from '../types';
import { titleCaseName, titleCaseNameOr } from '../lib/titleCase';
import { PASSED_HINT } from '../lib/checkIns';
import { averageAvailability, isWithdrawn, WITHDRAWN_HINT, WITHDRAWN_LABEL } from '../lib/withdrawn';
import { attendanceStatusLabel } from '../lib/attendanceLabels';

const STATUS_TONE: Record<AttendanceDay['status'], string> = {
  present: 'bg-emerald-100 text-emerald-700',
  half_day: 'bg-amber-100 text-amber-700',
  absent: 'bg-red-100 text-red-700',
  leave: 'bg-blue-100 text-blue-700',
  holiday: 'bg-gray-100 text-gray-600',
  weekly_off: 'bg-gray-100 text-gray-600',
  unmonitored: 'bg-slate-100 text-slate-600',
};

/**
 * Where the shift was clocked in from, in words.
 *
 * "unverified" is shown as its own thing rather than folded into "away": a
 * weak GPS fix and a person somewhere else are different facts, and the whole
 * geo module is built on not confusing them.
 */
function startedFrom(day: AttendanceDay): string {
  if (!day.startLocationStatus) return '—';
  if (day.startLocationStatus === 'unverified') return 'not verified';
  if (day.startLocationStatus === 'inside') return 'at workplace';
  const km = day.startDistanceM != null ? day.startDistanceM / 1000 : null;
  if (km == null) return 'away';
  return km >= 1 ? `${km.toFixed(1)} km away` : `${Math.round(km * 1000)} m away`;
}

/**
 * How late the day's first shift was started.
 *
 * null and 0 are different and both render as nothing: null means no assigned
 * start applied at all (no shift policy, a weekly off, a holiday, leave), and 0
 * means inside the policy's grace. Only a real overrun is worth a manager's
 * attention, and showing "on time" on every row would bury it.
 */
function startedLate(day: AttendanceDay): string | null {
  const late = day.lateByMinutes;
  if (late == null || late <= 0) return null;
  if (late < 60) return `${late} min late`;
  const hours = Math.floor(late / 60);
  const minutes = late % 60;
  return minutes ? `${hours}h ${minutes}m late` : `${hours}h late`;
}

/**
 * How long somebody has been in their meeting, in words.
 *
 * Recomputed on each render from the start time rather than stored, so it
 * stays honest between polls instead of freezing at whatever it said when the
 * response arrived.
 */
function meetingFor(member: LiveTeamMember): string | null {
  if (!member.meetingType || !member.meetingStartedAt) return null;
  const minutes = Math.max(
    0,
    Math.round((Date.now() - new Date(member.meetingStartedAt).getTime()) / 60000),
  );
  const label = member.meetingType === 'client' ? 'Client' : 'Team';
  return `In meeting (${label}) — ${minutes} min`;
}

export default function TeamPage() {
  const [workDate, setWorkDate] = useState(() => new Date().toISOString().slice(0, 10));
  /** Whose profile the slide-out is showing, by user id. */
  const [viewing, setViewing] = useState<string | null>(null);
  /** Which day's breakdown is open, by attendance row id. */
  const [openingDay, setOpeningDay] = useState<AttendanceDay | null>(null);

  const { data, isLoading } = useQuery<Page<AttendanceDay>>({
    queryKey: ['team-attendance', workDate],
    queryFn: async () =>
      (
        await client.get('/api/v1/attendance/team', {
          params: { workDate: workDate || undefined, limit: 100 },
        })
      ).data,
  });

  /**
   * Who is on shift right now.
   *
   * Its own request because the table above reads AttendanceDay, and that row
   * is only written when a shift ends — so anyone still working, which is
   * everyone this is about, is absent from it.
   */
  const { data: live } = useQuery<LiveTeamMember[]>({
    queryKey: ['team-live'],
    queryFn: async () => (await client.get('/api/v1/attendance/team/live')).data,
    refetchInterval: 60_000,
  });

  const inMeeting = (live ?? []).filter((m) => m.meetingType);
  const rows = data?.items ?? [];
  const present = rows.filter((r) => r.status === 'present').length;
  const flagged = rows.filter((r) => r.anomalyFlags && r.anomalyFlags.length > 0).length;
  // Withdrawn days are left out. An average is exactly where a withdrawn
  // figure gets asserted again without anybody choosing to -- the day itself
  // shows no number and the tile above it quietly keeps counting the one we
  // withdrew. Null rather than 0 when nothing is left: no days to average is
  // not the same finding as everybody scoring zero.
  const avgAvailability = averageAvailability(rows);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="label" htmlFor="date">Date</label>
          <input
            id="date"
            type="date"
            className="input"
            value={workDate}
            onChange={(e) => setWorkDate(e.target.value)}
          />
        </div>
      </div>

      {/* Above the table on purpose. The table is yesterday and earlier —
          an AttendanceDay row is written when a shift ends, so nobody still
          working appears in it. This is the only part of the page that is
          about right now. */}
      {inMeeting.length > 0 && (
        <div className="rounded-xl bg-indigo-50 p-3 ring-1 ring-indigo-200">
          <ul className="flex flex-wrap gap-x-4 gap-y-1">
            {inMeeting.map((member) => (
              <li key={member.userId} className="text-sm text-indigo-900">
                <span className="font-medium">{titleCaseName(member.userName) ?? member.userId}</span>
                {' — '}
                {meetingFor(member)}
                {member.meetingNote ? (
                  <span className="text-indigo-700"> · {member.meetingNote}</span>
                ) : null}
              </li>
            ))}
          </ul>
          {/* So nobody reads a paused check-in as a missed one. */}
          <p className="mt-1.5 text-[11px] text-indigo-700">
            Check-ins are paused while a meeting runs. They are excluded from the
            day rather than counted as missed.
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Metric label="Records" value={String(rows.length)} />
        <Metric label="Present" value={String(present)} />
        <Metric
          label="Avg availability"
          // Em dash, not 0%. Nothing left to average is not everybody scoring
          // zero, and this tile is read at a glance by somebody who will not
          // stop to wonder which it means.
          value={
            avgAvailability === null ? '—' : `${avgAvailability.toFixed(0)}%`
          }
        />
        <Metric
          label="Flagged for review"
          value={String(flagged)}
          tone={flagged > 0 ? 'warn' : 'neutral'}
        />
      </div>

      {isLoading ? (
        <div className="flex justify-center py-10 text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl bg-white ring-1 ring-gray-200">
          <table className="w-full text-sm">
            <thead className="border-b border-gray-200 bg-gray-50 text-left text-xs text-gray-500">
              <tr>
                <th className="px-4 py-2.5 font-medium">User</th>
                <th className="px-4 py-2.5 font-medium">Status</th>
                <th className="px-4 py-2.5 font-medium">Started</th>
                <th className="px-4 py-2.5 font-medium">Availability</th>
                <th className="px-4 py-2.5 font-medium">Check-ins</th>
                {/* Answered, then passed. A supervisor reading a low fraction
                    asks why, and a meeting is the answer often enough that it
                    belongs on the row rather than a click away. */}
                <th className="px-4 py-2.5 font-medium" title={PASSED_HINT}>
                  Passed
                </th>
                <th className="px-4 py-2.5 font-medium">Started From</th>
                <th className="px-4 py-2.5 font-medium">On Site</th>
                <th className="px-4 py-2.5 font-medium">Review</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((day) => (
                <tr key={day.id} className="hover:bg-gray-50">
                  {/* The name, not the raw user id this column used to
                      print — and clickable, since "who is this" is the first
                      question anyone asks of an attendance row. */}
                  <td className="px-4 py-2.5 text-gray-900">
                    <button
                      type="button"
                      className="text-left font-medium hover:text-indigo-700 hover:underline"
                      onClick={() => setViewing(day.userId)}
                      title="Open Profile"
                    >
                      {titleCaseName(day.userName) ?? day.userId}
                    </button>
                    {/* Two different questions from one row: who is this
                        person, and what happened on this day. The name answers
                        the first; this answers the second. */}
                    <button
                      type="button"
                      className="block text-left text-xs text-gray-500 hover:text-indigo-700 hover:underline"
                      onClick={() => setOpeningDay(day)}
                      title="Open This Day's Breakdown"
                    >
                      View day
                    </button>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={`badge ${STATUS_TONE[day.status]}`}>
                      {attendanceStatusLabel(day.status)}
                    </span>
                  </td>
                  {/* Informational, like the column beside it. A late
                      start was never refused, and this does not feed the
                      day's status or anything that reaches pay. */}
                  <td className="whitespace-nowrap px-4 py-2.5">
                    {startedLate(day) ? (
                      <span className="text-amber-700">{startedLate(day)}</span>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 tabular-nums text-gray-700">
                    {isWithdrawn(day) ? (
                      <span
                        className="text-xs text-amber-700"
                        title={day.scoringWithdrawnReason ?? WITHDRAWN_HINT}
                      >
                        {WITHDRAWN_LABEL}
                      </span>
                    ) : (
                      `${Number(day.availabilityPct).toFixed(0)}%`
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-gray-600">
                    {day.respondedPings}/{day.expectedPings}
                  </td>
                  <td
                    className="px-4 py-2.5 tabular-nums text-gray-600"
                    title={PASSED_HINT}
                  >
                    {day.excusedPings ? day.excusedPings : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                  {/* Recorded, never enforced — the shift started either
                      way. This is here so a manager can ask, not so the
                      system can refuse. */}
                  <td className="whitespace-nowrap px-4 py-2.5 text-gray-600">
                    {startedFrom(day)}
                  </td>
                  <td className="px-4 py-2.5 text-gray-600">
                    {day.presencePct != null
                      ? `${Number(day.presencePct).toFixed(0)}%`
                      : '—'}
                  </td>
                  <td className="px-4 py-2.5">
                    {day.anomalyFlags && day.anomalyFlags.length > 0 ? (
                      <span
                        className="inline-flex items-center gap-1 text-amber-600"
                        // Named, not the raw strings. A count is the
                        // one form of this nobody can act on; the
                        // drill-down lists them per check-in.
                        title={flagLabels(day.anomalyFlags)}
                      >
                        <ShieldAlert className="h-4 w-4" />
                        <span className="text-xs">
                          {day.anomalyFlags.length} flag
                          {day.anomalyFlags.length > 1 ? 's' : ''}
                        </span>
                      </span>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-400">
                    No attendance recorded for this date.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {viewing && (
        <EmployeeDetailPanel
          key={viewing}
          userId={viewing}
          onClose={() => setViewing(null)}
        />
      )}

      {openingDay && (
        <AttendanceDetailPanel
          key={openingDay.id}
          userId={openingDay.userId}
          workDate={openingDay.workDate}
          name={titleCaseNameOr(openingDay.userName, 'this employee')}
          onClose={() => setOpeningDay(null)}
        />
      )}
    </div>
  );
}

function Metric({
  label,
  value,
  tone = 'neutral',
}: {
  label: string;
  value: string;
  tone?: 'neutral' | 'warn';
}) {
  return (
    <div className="card">
      <p className="text-sm text-gray-500">{label}</p>
      <p
        className={`mt-2 text-2xl font-semibold ${
          tone === 'warn' ? 'text-amber-600' : 'text-gray-900'
        }`}
      >
        {value}
      </p>
    </div>
  );
}
